import {
  FluidAddressFields,
  FluidFieldAlias,
  FluidFormElementConfig,
  FluidLanguage,
  FluidRowOptions,
  FluidTranslatableFieldOptions,
  FluidTranslationSet,
  getAtPath,
  getRootElement,
} from '@lmig/fluid-core';
import {
  i18nProvider,
  translate,
} from '@lmig/fluid-core/lib/i18n/translation.helpers';

import { Utils } from '../../utils/utils';
import { defaultAddressConfiguration } from './address-form.config.utils';

export type FluidAddressFormConfiguration = {
  fieldAlias?: FluidFieldAlias<FluidAddressFields>;
  translations?: FluidTranslationSet<FluidAddressFields>;
} & FluidFormElementConfig;

export class FluidAddressFormConfig {
  layout: FluidRowOptions[];
  elements: FluidFormElementConfig[];
  fieldAlias: FluidFieldAlias<FluidAddressFields>;
  translations: FluidTranslationSet<FluidAddressFields>;

  private language: FluidLanguage;

  constructor(
    public locale: string,
    private addressConfiguration: FluidAddressFormConfiguration
  ) {
    const { fieldAlias, translations } =
      FluidAddressFormConfig.setAddressFormConfiguration(
        this.addressConfiguration
      );
    this.fieldAlias = fieldAlias;
    this.translations = translations;

    try {
      const i18n: HTMLFluidI18nProviderElement =
        getRootElement<HTMLFluidI18nProviderElement>(i18nProvider);
      i18n.extend(this.translations).then(async () => {
        // not implemented yet
      });
    } catch (e) {
      Utils.consoleWarn({ tagName: 'fluid-address-form' } as HTMLElement, e);
      this.language = FluidLanguage.ENGLISH;
    }
  }

  /**
   * Sets the row options for the grid layout.
   * @param rowOptions
   */
  setRowOptions(rowOptions: FluidRowOptions[]): void {
    this.layout = rowOptions;
  }

  /**
   * Sets the element for this flavour of form.
   * @param elements
   */
  setElements(elements: FluidFormElementConfig[]): void {
    if (!elements.find((el) => el.id === 'country')) {
      throw new Error(
        'You MUST include a country lookup field to create the address form'
      );
    } else {
      this.elements = elements;
    }
  }

  /**
   * Creates a subtitle for the form, to section the rest of the address
   * away from the main country lookyp
   * @param dataPath
   * @param gridRow
   * @param gridColumn
   * @param language
   */
  title(
    dataPath: keyof FluidAddressFields,
    gridRow: number,
    gridColumn: number,
    language: FluidLanguage = this.language
  ) {
    const { label } = translate<
      FluidAddressFields,
      FluidTranslatableFieldOptions
    >(this.translations, dataPath, language);

    return {
      component: 'fluid-title',
      props: {
        level: 2,
        disableGutters: true,
      },
      gridRow,
      gridColumn,
      content: label,
    };
  }

  /**
   * Creates a single field with translatable label and helptext
   * @param dataPath
   * @param gridRow
   * @param gridColumn
   * @param language
   */
  field(
    dataPath: keyof FluidAddressFields,
    gridRow: number,
    gridColumn: number,
    language: FluidLanguage = this.language
  ) {
    const { label, helpText } = translate<
      FluidAddressFields,
      FluidTranslatableFieldOptions
    >(this.translations, dataPath, language);
    return {
      id: dataPath,
      dataPath: this.fieldAlias[dataPath],
      label,
      helpText,
      gridRow,
      gridColumn,
      inlineField: true,
    };
  }

  /**
   * Shorthand to translate an address field at a given path.
   * @param identifier
   * @param translationPath
   */
  translate(identifier: keyof FluidAddressFields, translationPath: string) {
    return getAtPath(
      translate<FluidAddressFields, FluidTranslatableFieldOptions>(
        this.translations,
        identifier
      ),
      translationPath
    );
  }

  private static setAddressFormConfiguration(
    addressFormConfiguration?: FluidAddressFormConfiguration
  ): FluidAddressFormConfiguration {
    const fieldAlias = {
      ...defaultAddressConfiguration.fieldAlias,
      ...addressFormConfiguration.fieldAlias,
    };
    const translations = {
      ...defaultAddressConfiguration.translations,
      ...addressFormConfiguration.translations,
    };

    return { fieldAlias, translations };
  }
}
